const Validate = require('common/helpers/validate')
const {onlyUnique} = require('common/helpers/only-unique')
const CourseTypes = require('./course-types')

/**
 * Static module returns ALL options relevant to Student Preferences:
 *  - defaultStudentTypes
 *  - defaultExistingStudents
 *  - defaultStudyOptions
 *  - defaultTeachingPeriods
 *  - defaultSummerWinterOptions
 * Instantiate the module with a specific Course setting, to provide filtered preferences, and validation
 *   let prefs = new StudentPreference(course)
 *   prefs.data = formData
 *   let errors = await prefs.validate()
 * Instantiated model, once provided with a course, has an "options" property, holding the preference options filtered to the
 * selected Course and selected preference availability. These return an array of objects, where each object has the label and value,
 * as used in Form Select fields:
 *  - options.studentTypes
 *  - options.placesOfResidence
 *  - options.studyOptions
 *  - options.existingStudents
 *  - options.commencementYears
 *  - options.startYears
 *  - options.teachingPeriods
 *  - options.summerWinter
 * Instantiated model also holds the form data values (assigned to "data", exposed as separate properties):
 *  - studentType
 *  - placeOfResidence
 *  - studyOption
 *  - existingStudent
 *  - commencementYear
 *  - startYear
 *  - teachingPeriod
 *  - summerWinter
 */
class StudentPreferences extends Validate {
   static forwardPlanningAllowedInYears = 5
   static defaultOfferingLocations = ['Murdoch', 'Rockingham', 'Mandurah']

   static defaultStudentTypes = [{
         courseloop: 'Domestic',
         label: 'Domestic Student',
         shortLabel: 'Domestic'
      },
      {
         courseloop: 'International',
         label: 'International Student',
         shortLabel: 'International'
      },
      {
         courseloop: 'Transnational',
         label: 'Transnational Student',
         shortLabel: 'Transnational'
      }
   ]

   static defaultStudyOptions = [{
         courseloop: 'Full Time - 12',
         workload: 12,
         label: 'Full Time (4 units or 12 points per semester/trimester)',
         shortLabel: 'Full Time'
      },
      {
         courseloop: 'Full Time - 9',
         workload: 9,
         label: 'Full Time (3 units or 9 points per semester/trimester)',
         shortLabel: 'Full Time'
      },
      {
         courseloop: 'Part Time',
         workload: 6,
         label: 'Part Time (2 units or 6 points per semester/trimester)',
         shortLabel: 'Part Time'
      }
   ]

   static defaultExistingStudents = [
      { label: 'Yes', value: 'Yes' },
      { label: 'No', value: 'No' }
   ]

   static defaultTeachingPeriods = [
      {
         courseloop: 'Semester 1',
         match: /^(S1|Semester 1|Semester January.*)$/,
         label: 'Semester 1',
         code: 'S1',
         order: 1,
         shortLabel: 'Sem 1',
         cutOff: 6,
         group: 'Semester'
      },
      {
         courseloop: 'Winter Season',
         match: /^(W|Winter|Winter Season)$/,
         label: 'Winter',
         code: 'W',
         order: 2,
         shortLabel: 'Winter',
         cutOff: null,
         group: 'Semester'
      },
      {
         courseloop: 'Semester 2',
         match: /^(S2|Semester 2|Semester June.*)$/,
         label: 'Semester 2',
         code: 'S2',
         order: 3,
         shortLabel: 'Sem 2',
         cutOff: null,
         group: 'Semester'
      },
      {
         courseloop: 'Summer Season',
         match: /^(SUM|Summer|Summer Season)$/,
         label: 'Summer',
         code: 'SUM',
         order: 4,
         shortLabel: 'Summer',
         cutOff: null,
         group: 'Semester'
      },
      {
         courseloop: 'Trimester 1',
         match: /^(T1|Trimester 1|Trimester January.*)$/,
         label: 'Trimester 1',
         code: 'T1',
         order: 1,
         shortLabel: 'Trim 1',
         cutOff: 3,
         group: 'Trimester'
      },
      {
         // courseloop: 'Trimester April – July',
         courseloop: 'Trimester 2',
         match: /^(T2|Trimester 2|Trimester April.*)$/,
         label: 'Trimester 2',
         code: 'T2',
         order: 2,
         shortLabel: 'Trim 2',
         cutOff: 7,
         group: 'Trimester'
      },
      {
         courseloop: 'Trimester 3',
         match: /^(T3|Trimester 3|Trimester August.*)$/,
         label: 'Trimester 3',
         code: 'T3',
         order: 3,
         shortLabel: 'Trim 3',
         cutOff: null,
         group: 'Trimester'
      }
   ]

   static defaultSummerWinterOptions = [
      { label: 'Yes', value: 'Yes' },
      { label: 'No', value: 'No' }
   ]

   /**
    * Initial Form Schema for the student preferences form fields. This is updated with
    * different select options based on Course and Preferences selected.
    */
   schema = {
      studentType: {
         type: 'string',
         required: true,
         in: null
      },
      placeOfResidence: {
         type: 'string',
         requiredWhen: ['studentType', 'Transnational'],
         in: null
      },
      studyOption: {
         type: 'string',
         required: true,
         in: null
      },
      existingStudent: {
         type: 'string',
         required: true
      },
      commencementYear: {
         type: 'string',
         requiredWhen: ['existingStudent', 'Yes'],
         in: null
      },
      startYear: {
         type: 'string',
         required: true,
         in: null
      },
      teachingPeriod: {
         type: 'string',
         required: true,
         in: null
      },
      summerWinter: {
         type: 'string',
         required: true,
         in: null
      },
   }

   /**
    * Returns the Validation error messages for the student preferences form fields.
    * @returns {object} Validation field messages
    */
   get messages() {
      return {
         'studentType.required': 'Please select a valid student type',
         'studentType.in': 'Selected Student Type is no longer available for this course',
         'placeOfResidence.requiredWhen': 'Please select a place of residence',
         'placeOfResidence.in': 'Selected Place of Residence is no longer available for this course',
         'studyOption.required': 'Please select a study option',
         'studyOption.in': 'Selected Study option is not longer available for this course',
         'existingStudent.required': 'Please confirm if you are an existing student',
         'commencementYear.requiredWhen': 'Please select a valid commencement year',
         'commencementYear.in': 'Selected Commencement Date is no longer available for this course',
         'startYear.required': 'Please select a valid start year',
         'startYear.in': 'Selected Start Date is no longer available for this course',
         'teachingPeriod.required': 'Please select a teaching period',
         'teachingPeriod.in': 'Selected Study period is no longer available for this course',
         'summerWinter.required': 'Please confirm if you wish to study during Summer/Winter',
         'summerWinter.in': 'Selected Summer/Winter option is no longer available',
      }
   }

   /**
    * Student Preferences constructor, taking a course object as parameter to provide appropriate preferences
    * for the given course.
    */
   constructor() {
      // console.log('Initting Prefs', course && course.id || '-')
      super()
      // Default "options" for form select fields
      this.options = {
         studentTypes: [],
         placesOfResidence: [],
         studyOptions: [],
         existingStudents: [],
         commencementYears: [],
         startYears: [],
         teachingPeriods: [],
         summerWinter: []
      }
      this.cache = {
         placesOfResidence :[],
         commencementYears: [],
         commencementYearsDependency: '',
         startYears: [],
         teachingPeriods: []
      }
   }
   get data() {
      return super.data
   }

   /**
    * Set the Form data, and also update Form options based on current form selections
    */
   set data(data) {
      super.data = data
   }

   /**
    * Set the Course context for the selected course, and update the Form options based on offerings
    * for the selected course.
    */
   set course(course) {
      this._course = course
   }

   get course() {
      return this._course
   }

   /**
    * A helper function to map array into array of options for input
    * Specifically used to expose courseloop value as the key
    * @param {Object} item the array item to be mapped
    * @returns item with label as label and courseloop as value
    */
   static mapToCourseloopOption(item) {
      return {
         label: item.label,
         value: item.courseloop
      }
   }

   /**
    * A helper function to return item based on its courseloop value or pattern match value
    * @param {Object} item the item to be checked
    * @param {String} value the courseloop value to be compare against
    * @returns Boolean whether the given value match with item courseloop value or pattern match
    */
   static findByCourseloopOption(item, value) {
      return item.courseloop === value || (item.match && item.match.test(value))
   }

   /**
    * Given a Courseloop option identifier, returns the matching Student Type object.
    * @param {string} Courseloop Student Type
    * @returns {object} Student Type Object
    */
    static courseloopOptionToStudentType(courseloopOption) {
      return StudentPreferences.defaultStudentTypes.find((item) => StudentPreferences.findByCourseloopOption(item, courseloopOption))
   }

   /**
    * Given a Courseloop option identifier, returns the matching Student Type object.
    * @param {string} Courseloop Student Type
    * @returns {object} Student Type Object
    */
    static courseloopOptionToStudyOption(courseloopOption) {
      return StudentPreferences.defaultStudyOptions.find((item) => StudentPreferences.findByCourseloopOption(item, courseloopOption))
   }

   /**
    * Given a Courseloop option identifier, returns the matching Student Type object.
    * @param {string} Courseloop Student Type
    * @returns {object} Student Type Object
    */
    static courseloopOptionToTeachingPeriod(courseloopOption) {
      return StudentPreferences.defaultTeachingPeriods.find((item) => StudentPreferences.findByCourseloopOption(item, courseloopOption))
   }


   /**
    * Helper function to get an array of default teaching scheduled code for
    * course plan builder given a selected courseloop teaching period.
    * If no match can be found, S1 and S2 will be returned by default
    * @param {String} courseloopTeachingPeriod The courseloop teaching period
    * @returns Array of teaching period code usable by course plan builder.
    */
    static getSelectedTeachingScheduledGroup(courseloopTeachingPeriod, hasSummerWinter) {
      const startTeachingPeriod = this.defaultTeachingPeriods.find(item => this.findByCourseloopOption(item, courseloopTeachingPeriod))
      let teachingPeriods = this.defaultTeachingPeriods
      if (startTeachingPeriod) {
         teachingPeriods = teachingPeriods.filter(x => x.group == startTeachingPeriod.group)
      }
      const summerWinterCodes = ['SUM','W']
      if (hasSummerWinter !== 'Yes') {
         teachingPeriods = teachingPeriods.filter(x => !summerWinterCodes.some(p => p == x.code))
      }
      return teachingPeriods
   }

   /**
    * Helper function to get an array of default teaching scheduled code for
    * course plan builder given a selected courseloop teaching period.
    * If no match can be found, S1 and S2 will be returned by default
    * @param {String} courseloopTeachingPeriod The courseloop teaching period
    * @returns Array of teaching period code usable by course plan builder.
    */
   static getDefaultTeachingScheduled(courseloopTeachingPeriod, hasSummerWinter) {
      return StudentPreferences.getSelectedTeachingScheduledGroup(courseloopTeachingPeriod, hasSummerWinter).map(item => item.code)
   }

   /**
    * Returns an object of the valid teaching schedules for the current Course, broken down by year, period and location.
    * ie; {"year": {"teaching period" : ["locations"] }}
    * @returns {object} Course Teaching Schedules
    */
   getCourseTeachingSchedules() {
      if (!this.course) {
         return {}
      }

      // get max year from list of selectable start year
      const maxYear = new Date().getFullYear() + 1

      const teachingSchedules = this.course.courseOfferings.reduce((schedule, offering) => {
         if (offering.startYear && offering.teachingScheduled) {
            // check end year
            let endYear = maxYear
            if (offering.endYear) {
               endYear = offering.endYear
            }
            for (let i = offering.startYear; i <= endYear; i++) {
               if (!schedule[i]) {
                  schedule[i] = {}
               }

               if (!Object.keys(schedule[i]).includes(offering.teachingScheduled)) {
                  schedule[i][offering.teachingScheduled] = []
               }

               if (schedule[i][offering.teachingScheduled].findIndex((loc) => loc.id === offering.location.id) < 0) {
                  schedule[i][offering.teachingScheduled].push({
                     ...offering.location
                  })
               }
            }
         }
         return schedule
      }, {})

      return teachingSchedules
   }

   /**
    * Helper function to get an array of the valid start dates for the current Course and current selected starting year
    * @param {Number} startYear optional parameter for start date year. if not provided, the current set startYear will be used
    * @returns {array} Course Start Date Periods
    */
   getCourseStartDatePeriod(startYear) {
      if (!startYear) {
         startYear = this.startYear
      }
      const courseTeachingSchedules = this.getCourseTeachingSchedules()
      if (!startYear || !courseTeachingSchedules || !Object.keys(courseTeachingSchedules).length) {
         return ['N/A']
      }
      let startYearKey = parseInt(startYear)
      // find the earliest year occurrence in validTeachingSchedules
      const validYears = Object.keys(courseTeachingSchedules).map(year => parseInt(year))
      const minYear = Math.min.apply(Math, validYears)
      while (validYears.indexOf(startYearKey) < 0 && startYearKey >= minYear) {
         startYearKey--
      }

      // filter by locations
      let validStartDatePeriod = []
      const teachingPeriods = courseTeachingSchedules[startYearKey]
      if (teachingPeriods) {
         const validLocations = StudentPreferences.getCourseLocations(this.course, this.studentType, this.placeOfResidence)
         validStartDatePeriod = Object.keys(teachingPeriods).filter((key) => {
            return teachingPeriods[key].filter((loc) => validLocations.indexOf(loc.id) !== -1).length
         })
      }

      if (!validStartDatePeriod.length) {
         return ['N/A']
      }

      return validStartDatePeriod
   }

   /**
    *  Helper function to get an array of the valid course location id for the current Course and current selected student type
    * @param {Object} course the course
    * @param {String} studentType the course
    * @param {String} placeOfResidence the course
    * @returns {array} Course Location Ids
    */
   static getCourseLocations(course, studentType, placeOfResidence) {
      if (studentType === 'Transnational') {
         return [placeOfResidence || 'N/A']
      } else {
         return course.courseOfferings.reduce((locations, offering) => {
            // add all murdoch locations as valid location
            if (StudentPreferences.defaultOfferingLocations.includes(offering.location.name) && !locations.includes(offering.location.id)) {
               locations.push(offering.location.id)
            }
            return locations
         }, [])
      }
   }


   /**
    * Helper function to return an array of teaching period that is applicable at
    * the start of the course. This is determined by default teaching period and its order
    * e.g. if Semester 2 was passed as courseloop teaching period, this function will only return S2
    * However if Semester 1 was passed, this function will return S1 and S2
    * @param {String} courseloopTeachingPeriod The courseloop teaching period
    * @returns Array of teaching period code for the first year usable by course plan builder.
    */
   static getStartTeachingSchedule(courseloopTeachingPeriod, hasSummerWinter) {
      const startTeachingPeriod = this.defaultTeachingPeriods.find(item => this.findByCourseloopOption(item, courseloopTeachingPeriod))
      const startOrder = startTeachingPeriod.order
      return StudentPreferences.getSelectedTeachingScheduledGroup(courseloopTeachingPeriod, hasSummerWinter)
      .filter(item => item.order >= startOrder)
      .map(item => item.code)
   }

   /**
    * A helper function to map parameters to fee model
    * @param {String} studentType Domestic, International or Transnational
    * @param {String} marketedCourseType marketed course type from courseloop
    * @param {Boolean} commonwealthSupported boolean that's returned from course api
    * @param {Number} commencementYear the commencement year of the course
    */
   static mapToFeeModel(studentType, marketedCourseType, commonwealthSupported, commencementYear) {

      // student type mapping
      studentType = studentType + ' Student'

      // course type mapping
      const foundCourseType = CourseTypes.find(t => {
         return t.courseloop.toLowerCase().indexOf(marketedCourseType.toLocaleLowerCase()) !== -1
      })
      let courseType = foundCourseType ? foundCourseType.feeCourseType : ''

      //dont map the commencementYear, we have no idea how to map it client side.

      // fee type mapping
      let feeType = 'Full Fee Paying'
      if (studentType === 'Domestic Student') {
         if (courseType === 'Research Degree') {
            feeType = '-'
         } else if (commonwealthSupported) {
            feeType = 'Commonwealth Supported Place'
         }
      }

      return {
         studentType,
         courseType,
         feeType,
         commencementYear
      }
   }

   async setData(data) {
      super.data = data
      if (data) await this.updateOptions()
   }

   async setCourse(course) {
      this._course = course;
      await this.updateOptions();
   }

   /**
    * This should be overridden by methods in the Client-side and Server-side extensions of this base class
    */
   async getFeesPotentialStudentTypes() {
      throw new Error('Requires StudentPreferences class with Fees API access.')
   }

   /**
    * This should be overridden by methods in the Client-side and Server-side extensions of this base class
    */
    async getStudentPreferenceLocations() {
      throw new Error('Requires StudentPreferences class with Server API access.')
   }
   /**
    * Recalculate preferences valid options based on course and other form data.
    * Derives array of valid values, then converts it into array of options as used
    * by Select fields.
    * Updates the validation schema with valid field values.
    */
   async updateOptions() {
      //transational fix for existing student type - this needs to go into validation calls.
      if (this.studentType === 'Transnational') {
         this.existingStudent = 'No'
      }

      //STUDENT TYPES
      const courseStudentTypes = this.courseStudentTypes()
      //STUDENT TYPES - Update Schema valid values
      this.schema.studentType.in = courseStudentTypes
      //STUDENT TYPES - Update Form select options
      this.options.studentTypes = StudentPreferences.defaultStudentTypes
         .filter(type => courseStudentTypes.includes(type.courseloop))
         .map(StudentPreferences.mapToCourseloopOption)

      //PLACE OF RESICENCE
      const placesOfResidence = await this.coursePlaceOfResidence()
      //PLACE OF RESICENCE - Update Form select options
      this.schema.placeOfResidence.in = placesOfResidence
      //PLACE OF RESICENCE - Update Form select options
      this.options.placesOfResidence = this.cache.placesOfResidence
         .map(StudentPreferences.mapToCourseloopOption)

      //STUDY OPTIONS
      const courseStudyOptions = this.courseStudyOptions()
      //STUDY OPTIONS - Update Schema valid values
      this.schema.studyOption.in = courseStudyOptions
      //STUDY OPTIONS - Update Form select options
      this.options.studyOptions = StudentPreferences.defaultStudyOptions
         .filter(type => courseStudyOptions.includes(type.courseloop))
         .map(StudentPreferences.mapToCourseloopOption)

      //EXISTING STUDENT SELECTION
      const courseExistingStudentOptions = this.courseExistingStudent()
      //EXISTING STUDENT SELECTION - Update Schema valid values
      this.schema.existingStudent.in = courseExistingStudentOptions
      //EXISTING STUDENT SELECTION - Update Form select options
      this.options.existingStudents = StudentPreferences.defaultExistingStudents

      //COMMENCEMENT YEAR
      const courseCommencementYears = await this.courseCommencementYears()
      //COMMENCEMENT YEAR - Update Schema valid values
      if (this.existingStudent == 'No') // No commencementYear selection requirement if not an Existing Student
         delete this.schema.commencementYear.in
      else
         this.schema.commencementYear.in = courseCommencementYears
      //COMMENCEMENT YEAR - Update Form select options
      this.options.commencementYears = this.cache.commencementYears

      //START YEAR
      const courseStartYears = this.courseStartYears()
      //START YEAR - Update Schema valid values
      this.schema.startYear.in = courseStartYears
      //START YEAR - Update Form select options
      this.options.startYears = this.cache.startYears

      // //TEACHING PERIODS
      const courseTeachingPeriods = this.courseTeachingPeriods()
      //TEACHING PERIODS - Update Schema valid values
      this.schema.teachingPeriod.in = courseTeachingPeriods
      //TEACHING PERIODS - Update Form select options
      this.options.teachingPeriods = this.cache.teachingPeriods


      //SummerWinter SELECTION
      const courseSummerWinterOptions = this.courseSummerWinterOptions()

      //EXISTING STUDENT SELECTION - Update Schema valid values
      if (this.teachingPeriod && this.teachingPeriod.indexOf('Semester') > -1) // No commencementYear selection requirement if not an Existing Student
      {
         this.schema.summerWinter.in = courseSummerWinterOptions
         this.schema.summerWinter.required = true;
      }
      else
      {
         delete this.schema.summerWinter.in
         delete this.schema.summerWinter.required
      }

      //EXISTING STUDENT SELECTION - Update Form select options
      this.options.summerWinter = StudentPreferences.defaultSummerWinterOptions
   }

   /**
    * Returns an array of the valid student types for the current Course.
    * @returns {array} Course Student Types
    */
   courseStudentTypes() {
      if (!this.course) {
         return ['N/A']
      }

      return this.course.courseOfferings.map(offering => offering.studentTypes).flat(1).filter(types => types).filter(onlyUnique)
   }

   /**
    * Returns an array of the valid study options for the current Course.
    * @returns {array} Course Study Options
    */
   courseStudyOptions() {
      if (!this.course) {
         return ['N/A']
      }

      const studyOptions = this.course.attendanceTypes.flat(1).filter(types => types).filter(onlyUnique)

      let fullTimeIndex = studyOptions.findIndex(option => option === 'Full Time')
      // if Full Time is found to be one of available study options
      // we need to include both Full Time - 12 and Full Time - 9 options
      if (fullTimeIndex >= 0) {
         studyOptions[fullTimeIndex] = 'Full Time - 12'
         studyOptions.push('Full Time - 9')
      }
      if (!studyOptions.length)
         return ['N/A']
      return studyOptions
   }

   async coursePlaceOfResidence() {
      //map to the course places of residence, only return valid options
      if (!this.cache.placesOfResidence || this.cache.placesOfResidence.length == 0) {
         const possiblePlaceOfResidence = await this.getStudentPreferenceLocations();
         this.cache.placesOfResidence = possiblePlaceOfResidence;
      }

      return this.cache.placesOfResidence.length > 0 ? this.cache.placesOfResidence.map(m => m.courseloop) : ["N/A"]
   }

   courseExistingStudent() {
      //this is always static, just return the valid list
      return StudentPreferences.defaultExistingStudents.map(m => m.label);
   }

   courseSummerWinterOptions() {
      //this is always static, just return the valid list
      return StudentPreferences.defaultSummerWinterOptions.map(m => m.label);
   }

   async courseCommencementYears() {
      if (this.course && this.existingStudent) {

         if (!this.cache.commencementYears ||
            this.cache.commencementYearsDependency != (this.studentType + '' + this.existingStudent)) {
            // convert study preference to fee input model and load to the cache
            let feeModel = StudentPreferences.mapToFeeModel(this.studentType, this.course.classification.marketedCourseType,
               this.course.commonwealthSupported, null)

            const possibleStudentTypes = await this.getFeesPotentialStudentTypes(feeModel.studentType, feeModel.feeType,
               feeModel.courseType)

            this.cache.commencementYears = possibleStudentTypes.map(x => x.commencementYear).reduce((acc, curr) => {
                  if (!acc.find(x => x == curr)) {
                     acc.push(curr)
                  }
                  return acc
               }, []).map(x => ({label: x,value: x}))
         }
         this.cache.commencementYearsDependency = (this.studentType + '' + this.existingStudent);

         return this.cache.commencementYears.length > 0 ? this.cache.commencementYears.map(m => m.label) : ["N/A"]
      }
      return ['N/A']
   }

   courseStartYears() {
      if (this.course && this.studentType) {
         // Calculate valid start years
         const courseTeachingSchedules = this.getCourseTeachingSchedules()
         const validYears = Object.keys(courseTeachingSchedules).filter(key => !isNaN(parseInt(key)))
         let minYear = validYears.length ? Math.min.apply(Math, validYears) : -1

         //selection options are this year (or minimum course year - if greater than this year)
         minYear = Math.max(new Date().getFullYear(), minYear);

         //TODO - allow planning up to 'forwardPlanningAllowedInYears'years in advance (or course end year / if any)
         const maxYear = new Date().getFullYear() + StudentPreferences.forwardPlanningAllowedInYears // max date is next year

         const startYears = []
         if (minYear !== -1) {
            for (let i = minYear; i <= maxYear; i++) {
               // checks if that year has a valid teaching periods
               const validPeriods = this.getCourseStartDatePeriod(i)
               if (validPeriods.filter(p => p !== 'N/A').length) {
                  startYears.push({label: i.toString(),value: i.toString()})
               }
            }
         }

         this.cache.startYears = startYears;

         return this.cache.startYears.length > 0 ? this.cache.startYears.map(m => m.label) : ["N/A"]
      }
      return ['N/A']
   }

   courseTeachingPeriods() {
      if (this.course && this.startYear) {
         let courseStartDates = this.getCourseStartDatePeriod(this.startYear);

         let nowYear = new Date().getFullYear()
         let nowMonth = new Date().getMonth()

         let teachingPeriods = StudentPreferences.defaultTeachingPeriods
            .filter(type => courseStartDates.some(date => StudentPreferences.findByCourseloopOption(type, date)))

         if (this.startYear <= nowYear) {
            teachingPeriods = teachingPeriods.filter(period => (period.cutOff == null || nowMonth < period.cutOff))
         }

         this.cache.teachingPeriods = teachingPeriods.map(StudentPreferences.mapToCourseloopOption);

         return this.cache.teachingPeriods.length > 0 ? this.cache.teachingPeriods.map(m => m.value) : ["N/A"]
      }
      return ['N/A']
   }

}

module.exports = StudentPreferences
